// API Configuration
const API_BASE_URL = 'https://shopify-store-omega.vercel.app';

// Collection version - change this when updating to a new collection
// This will clear old ratings on all devices automatically
const COLLECTION_VERSION = 'v9';

class RatingsPage {
  constructor(container) {
    this.container = container;
    this.sectionId =
      container.dataset.sectionId || container.id.replace("ratings-page-", "");
    this.productRatings = {}; // Store ratings for each product: { productId: rating }
    this.currentProductIndex = 0;
    this.products = [];
    this.totalProducts = 0;
    this.hasShared = false;
    this.hasReceivedCoupon = false;
    this.hasSubmitted = false; // Prevent duplicate submissions
    this.hasTrackedShare = false; // Prevent duplicate share tracking
    this.batch = container.dataset.batch || 'batch_1';
    this.discountCode = container.dataset.discountCode || "RATE10";
    this.shareMessage =
      container.dataset.shareMessage ||
      "Hey! One of my friends has started a lifestyle brand. Can you please help him by giving some feedback on the latest styles?";
    // Clean URL - remove query parameters like ?pb=0
    this.pageUrl = window.location.origin + window.location.pathname;
    this.campaign = this.resolveCampaign();

    this.init();
  }

  // Read ?c= (campaign tag) from the current URL and persist it.
  // Once captured, it sticks for the session so a refresh or a later
  // visit without the param keeps the same attribution.
  resolveCampaign() {
    const STORAGE_KEY = 'ratings_campaign';
    let campaign = null;
    try {
      const params = new URLSearchParams(window.location.search);
      const fromUrl = params.get('c');
      if (fromUrl) {
        campaign = fromUrl.slice(0, 32);
        localStorage.setItem(STORAGE_KEY, campaign);
      } else {
        campaign = localStorage.getItem(STORAGE_KEY);
      }
    } catch (e) {
      console.warn('Could not resolve campaign tag', e);
    }
    return campaign;
  }

  init() {
    // Wait a bit for DOM to be ready
    setTimeout(() => {
      // Check collection version - clear old ratings if collection changed
      this.checkCollectionVersion();
      
      this.loadProducts();
      this.setupStarRatings();
      this.setupNavigation();
      this.setupShareButtons();
      this.setupCopyButton();
      this.setupApplyDiscountButton();
      this.setupRateAgainButton();
      this.loadExistingRatings();
      this.updateProgress();
      this.showCurrentProduct(); // Ensure first product is visible
      
      // Check if user has already completed all ratings
      this.checkAndShowCompletionPage();
    }, 100);
  }

  checkCollectionVersion() {
    const storedVersion = localStorage.getItem(`collection_version_${this.sectionId}`);
    
    if (storedVersion !== COLLECTION_VERSION) {
      console.log(`Collection changed (${storedVersion} → ${COLLECTION_VERSION}). Clearing old ratings.`);
      
      // Clear all old rating data
      localStorage.removeItem(`ratings_${this.sectionId}`);
      localStorage.removeItem(`ratings_submissions_${this.sectionId}`);
      localStorage.removeItem(`has_submitted_${this.sectionId}`);
      localStorage.removeItem(`has_shared_${this.sectionId}`);
      localStorage.removeItem(`has_received_coupon_${this.sectionId}`);
      localStorage.removeItem('ratings_session_id');
      
      // Reset state
      this.productRatings = {};
      this.currentProductIndex = 0;
      this.hasSubmitted = false;
      this.hasTrackedShare = false;
      this.hasShared = false;
      this.hasReceivedCoupon = false;
      
      // Save new version
      localStorage.setItem(`collection_version_${this.sectionId}`, COLLECTION_VERSION);
    }
  }

  loadProducts() {
    const productCards = this.container.querySelectorAll(
      ".product-rating-card.sequential-card"
    );
    this.products = Array.from(productCards);

    // Randomize display order per session. data-product-id stays glued to each
    // card, so backend ratings remain keyed to the same shirt.
    this.shuffleProducts();

    this.totalProducts = this.products.length; // Dynamic total products to rate

    console.log(`Loaded ${this.totalProducts} products`); // Debug log

    // Update total products display
    const totalProductsElement = this.container.querySelector(
      `#total-products-${this.sectionId}`
    );
    if (totalProductsElement) {
      totalProductsElement.textContent = this.totalProducts;
    }

    // Ensure products are properly initialized - hide all except first
    if (this.products.length === 0) {
      console.error("No products found!");
    } else {
      console.log(`Found ${this.products.length} product cards`);
      // Use CSS classes only - no inline display styles
      this.products.forEach((card, index) => {
        card.style.removeProperty("display");
        card.classList.remove("active");
      });

      if (this.products[0]) {
        this.products[0].classList.add("active");
        
        // Ensure the randomly selected first image loads eagerly
        const firstImg = this.products[0].querySelector("img");
        if (firstImg) {
          firstImg.setAttribute("loading", "eager");
          firstImg.setAttribute("fetchpriority", "high");
        }
      }
    }
  }

  shuffleProducts() {
    if (this.products.length < 2) return;
    for (let i = this.products.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [this.products[i], this.products[j]] = [this.products[j], this.products[i]];
    }
    const parent = this.products[0].parentNode;
    if (parent) {
      this.products.forEach((card) => parent.appendChild(card));
    }
  }

  setupStarRatings() {
    const starButtons = this.container.querySelectorAll(".star-btn");

    starButtons.forEach((btn) => {
      const productId = btn.dataset.productId;

      btn.addEventListener("click", () => {
        const rating = parseInt(btn.dataset.rating);
        this.setRating(productId, rating);
      });
    });
  }

  setRating(productId, rating) {
    this.productRatings[productId] = rating;
    this.highlightStars(productId, rating);
    this.saveRatings();

    // Auto-advance to next product or finish after rating
    setTimeout(() => {
      if (this.currentProductIndex < this.products.length - 1) {
        this.nextProduct();
      } else {
        const allRated = this.products.every((card) => {
          const pid = card.dataset.productId;
          return this.productRatings[pid] > 0;
        });

        if (allRated) {
          this.trackSubmission();

          const ratingContainer = this.container.querySelector(
            `#rating-container-${this.sectionId}`
          );
          if (ratingContainer) ratingContainer.style.display = "none";

          this.showCouponCode();
        }
      }
    }, 250);
  }

  highlightStars(productId, count) {
    const starsContainer = this.container.querySelector(
      `#rating-stars-${this.sectionId}-${productId}`
    );
    if (!starsContainer) return;

    const starButtons = Array.from(
      starsContainer.querySelectorAll(".star-btn")
    );

    starButtons.forEach((btn, index) => {
      const svg = btn.querySelector("svg");
      if (index + 1 <= count) {
        btn.classList.add("active");
        if (svg) svg.setAttribute("fill", "#ffc107");
      } else {
        btn.classList.remove("active");
        if (svg) svg.setAttribute("fill", "#ddd");
      }
    });
  }

  setupNavigation() {
    // Next buttons
    const nextButtons = this.container.querySelectorAll(".next-product-btn");
    nextButtons.forEach((btn) => {
      btn.addEventListener("click", () => {
        this.nextProduct();
      });
    });

    // Previous buttons
    const prevButtons = this.container.querySelectorAll(".prev-product-btn");
    prevButtons.forEach((btn) => {
      btn.addEventListener("click", () => {
        this.prevProduct();
      });
    });

    // Finish button
    const finishBtn = this.container.querySelector(
      `#finish-rating-${this.sectionId}`
    );
    if (finishBtn) {
      finishBtn.addEventListener("click", () => {
        this.finishRating();
      });
    }
  }

  nextProduct() {
    // Get current product ID
    const currentProduct = this.products[this.currentProductIndex];
    const currentProductId = currentProduct?.dataset.productId;
    
    // Check if current product has been rated
    if (!this.productRatings[currentProductId]) {
      // Show error message - product not rated
      this.showRatingRequiredMessage(currentProductId);
      return; // Don't advance to next product
    }
    
    if (this.currentProductIndex < this.totalProducts - 1) {
      this.currentProductIndex++;
      console.log(
        `Moving to product ${this.currentProductIndex + 1} of ${
          this.totalProducts
        }`
      );
      this.showCurrentProduct();
      this.updateProgress();
    } else {
      console.log("Already on last product");
    }
  }

  prevProduct() {
    if (this.currentProductIndex > 0) {
      this.currentProductIndex--;
      console.log(
        `Moving to product ${this.currentProductIndex + 1} of ${
          this.totalProducts
        }`
      );
      this.showCurrentProduct();
      this.updateProgress();
    } else {
      console.log("Already on first product");
    }
  }

  showCurrentProduct() {
    if (this.products.length === 0) return;

    // Hide all cards and HARD reset stars + SVG fills
    this.products.forEach((card) => {
      card.classList.remove("active");

      card.querySelectorAll(".star-btn").forEach((btn) => {
        btn.classList.remove("active");
        // Direct SVG fill reset - bypasses CSS currentColor completely
        const svg = btn.querySelector("svg");
        if (svg) svg.setAttribute("fill", "#ddd");
      });
    });

    // Show current card
    const currentCard = this.products[this.currentProductIndex];
    if (!currentCard) return;

    currentCard.classList.add("active");

    // Preload next image for smoother experience
    if (this.currentProductIndex + 1 < this.products.length) {
      const nextCard = this.products[this.currentProductIndex + 1];
      const nextImg = nextCard.querySelector("img");
      if (nextImg && nextImg.getAttribute("loading") === "lazy") {
        nextImg.setAttribute("loading", "eager");
        nextImg.setAttribute("fetchpriority", "high");
      }
    }

    // Apply rating only for this card
    const productId = currentCard.dataset.productId;
    const rating = this.productRatings[productId] || 0;
    this.highlightStars(productId, rating);

    // Update counter
    const currentNumberElement = this.container.querySelector(
      `#current-product-number-${this.sectionId}`
    );
    if (currentNumberElement) {
      currentNumberElement.textContent = this.currentProductIndex + 1;
    }
  }

  updateNavigationButtons() {
    const currentProduct = this.products[this.currentProductIndex];
    if (!currentProduct) return;

    // Show/hide previous button
    const prevBtn = currentProduct.querySelector(".prev-product-btn");
    if (prevBtn) {
      prevBtn.style.display =
        this.currentProductIndex > 0 ? "inline-block" : "none";
    }

    // Update next/finish buttons
    this.updateNextButton();
  }

  updateProgress() {
    console.log('=== UPDATE PROGRESS CALLED ===');
    console.log('Current product index:', this.currentProductIndex);
    console.log('Total products:', this.totalProducts);
    console.log('Section ID:', this.sectionId);
    
    const progressFill = this.container.querySelector(
      `#progress-fill-${this.sectionId}`
    );
    
    console.log('Progress fill element:', progressFill);
    
    if (progressFill) {
      const progress =
        ((this.currentProductIndex + 1) / this.totalProducts) * 100;
      
      console.log('Calculated progress:', progress + '%');
      
      // Try multiple ways to set the width
      progressFill.style.setProperty('width', `${progress}%`, 'important');
      progressFill.setAttribute('style', `width: ${progress}% !important;`);
      progressFill.style.width = `${progress}%`;
      
      console.log('After setting - element style.width:', progressFill.style.width);
      console.log('After setting - computed style:', window.getComputedStyle(progressFill).width);
      console.log(`Progress updated: ${progress}% (Product ${this.currentProductIndex + 1} of ${this.totalProducts})`);
    } else {
      console.error('❌ Progress bar element NOT FOUND!');
      console.error('Looking for selector:', `#progress-fill-${this.sectionId}`);
      console.error('Container:', this.container);
      
      // Try to find it without section ID
      const allProgressFills = document.querySelectorAll('.progress-fill');
      console.log('Found progress-fill elements on page:', allProgressFills.length);
      allProgressFills.forEach((el, i) => {
        console.log(`  [${i}] id="${el.id}"`);
      });
    }
  }

  showRatingRequiredMessage(productId) {
    // Get the current product card
    const currentProduct = this.products[this.currentProductIndex];
    if (!currentProduct) return;

    // Find or create the error message element
    let errorMsg = currentProduct.querySelector('.rating-error-message');
    
    if (!errorMsg) {
      // Create error message
      errorMsg = document.createElement('div');
      errorMsg.className = 'rating-error-message';
      errorMsg.innerHTML = `
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
        <span>Please rate this product before continuing</span>
      `;
      
      // Insert before the navigation buttons
      const navigation = currentProduct.querySelector('.product-navigation');
      if (navigation) {
        navigation.parentNode.insertBefore(errorMsg, navigation);
      }
    }
    
    // Make it visible and shake it
    errorMsg.style.display = 'flex';
    errorMsg.classList.add('shake');
    
    // Remove shake animation after it completes
    setTimeout(() => {
      errorMsg.classList.remove('shake');
    }, 500);
    
    // Hide after 3 seconds
    setTimeout(() => {
      errorMsg.style.display = 'none';
    }, 3000);
  }

  updateNextButton() {
    const currentProduct = this.products[this.currentProductIndex];
    if (!currentProduct) {
      console.error(`No product at index ${this.currentProductIndex}`);
      return;
    }

    const productId = currentProduct.dataset.productId;
    const hasRating = this.productRatings[productId] > 0;

    // Show/hide next button - allow navigation even without rating (optional)
    const nextBtn = currentProduct.querySelector(".next-product-btn");
    const finishBtn = this.container.querySelector(
      `#finish-rating-${this.sectionId}`
    );

    if (this.currentProductIndex === this.totalProducts - 1) {
      // Last product
      if (nextBtn) nextBtn.style.display = "none";
      if (finishBtn) {
        // Show finish button if rated, or allow finishing without rating
        finishBtn.style.display = hasRating ? "inline-block" : "inline-block";
        finishBtn.textContent = hasRating
          ? "Finish Rating"
          : "Finish (Rate to Continue)";
      }
    } else {
      // Not last product - show next button
      if (nextBtn) {
        // Always show next button, but maybe disable if not rated
        nextBtn.style.display = "inline-block";
        if (!hasRating) {
          nextBtn.style.opacity = "0.6";
          nextBtn.title = "Rate this product to continue";
        } else {
          nextBtn.style.opacity = "1";
          nextBtn.title = "";
        }
      }
      if (finishBtn) finishBtn.style.display = "none";
    }
  }

  finishRating() {
    // Check if all products are rated
    const allRated = this.products.every((card) => {
      const productId = card.dataset.productId;
      return this.productRatings[productId] > 0;
    });

    if (!allRated) {
      alert("Please rate all products before finishing!");
      return;
    }

    // Track submission
    this.trackSubmission();

    // Hide rating container
    const ratingContainer = this.container.querySelector(
      `#rating-container-${this.sectionId}`
    );
    if (ratingContainer) ratingContainer.style.display = "none";

    // Show coupon directly (skip share)
    this.showCouponCode();
  }

  async trackSubmission() {
    // Prevent duplicate submissions
    if (this.hasSubmitted) {
      console.log('⚠️ Submission already tracked, skipping duplicate');
      return;
    }
    
    this.hasSubmitted = true;
    
    const submissions = this.getSubmissions();

    // Check for spam re-rating (same rating given to all shirts on a subsequent try)
    if (submissions.length > 0) {
      const ratingsArray = Object.values(this.productRatings);
      if (ratingsArray.length > 0) {
        const firstRating = ratingsArray[0];
        const isAllSame = ratingsArray.every(r => r === firstRating);
        
        if (isAllSame) {
          console.log('Spam re-rating detected: All shirts given the same rating. Ignoring this submission to keep previous valid data.');
          return; // Skip sending to DB and local storage
        }
      }
    }
    
    const submissionData = {
      timestamp: new Date().toISOString(),
      ratings: this.productRatings,
      totalProducts: this.totalProducts,
    };

    // Keep localStorage backup
    submissions.push(submissionData);
    localStorage.setItem(
      `ratings_submissions_${this.sectionId}`,
      JSON.stringify(submissions)
    );

    // Mark as submitted (but allow re-rating)
    localStorage.setItem(`has_submitted_${this.sectionId}`, "true");

    // NEW: Send to backend API
    try {
      const response = await fetch(`${API_BASE_URL}/api/ratings/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ratings: this.productRatings,
          sessionId: this.getSessionId(),
          campaign: this.campaign,
          metadata: {
            productTitles: this.getProductTitles(),
            pageUrl: window.location.href,
            campaign: this.campaign
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit ratings');
      }
      
      const result = await response.json();
      console.log('✅ Ratings saved to backend:', result);
    } catch (error) {
      console.error('❌ Error saving to backend:', error);
      // Fail gracefully - data is still in localStorage
    }
  }

  getSubmissions() {
    const saved = localStorage.getItem(`ratings_submissions_${this.sectionId}`);
    return saved ? JSON.parse(saved) : [];
  }

  showSharePrompt() {
    const sharePrompt = this.container.querySelector(
      `#share-prompt-container-${this.sectionId}`
    );
    if (sharePrompt) {
      sharePrompt.style.display = "block";

      // Set share message preview
      const shareMessagePreview = this.container.querySelector(
        `#share-message-preview-${this.sectionId}`
      );
      if (shareMessagePreview) {
        shareMessagePreview.textContent = this.shareMessage;
      }

      // Scroll to share prompt
      sharePrompt.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  setupShareButtons() {
    // WhatsApp
    const whatsappBtn = this.container.querySelector(
      `#share-whatsapp-${this.sectionId}`
    );
    if (whatsappBtn) {
      whatsappBtn.addEventListener("click", () => {
        this.shareOnWhatsApp();
      });
    }

    // Facebook
    const facebookBtn = this.container.querySelector(
      `#share-facebook-${this.sectionId}`
    );
    if (facebookBtn) {
      facebookBtn.addEventListener("click", () => {
        this.shareOnFacebook();
      });
    }

    // Twitter
    const twitterBtn = this.container.querySelector(
      `#share-twitter-${this.sectionId}`
    );
    if (twitterBtn) {
      twitterBtn.addEventListener("click", () => {
        this.shareOnTwitter();
      });
    }

    // Copy Link
    const copyLinkBtn = this.container.querySelector(
      `#share-copy-${this.sectionId}`
    );
    if (copyLinkBtn) {
      copyLinkBtn.addEventListener("click", () => {
        this.copyShareLink();
      });
    }
  }

  shareOnWhatsApp() {
    const message = encodeURIComponent(
      `${this.shareMessage}\n\nCheck it out: ${this.pageUrl}`
    );
    const url = `https://wa.me/?text=${message}`;
    window.open(url, "_blank", "width=600,height=400");
    this.markAsShared('whatsapp');
  }

  shareOnFacebook() {
    const message = encodeURIComponent(
      `${this.shareMessage}`
    );
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      this.pageUrl
    )}&quote=${message}`;
    window.open(url, "_blank", "width=600,height=400");
    this.markAsShared('facebook', );
  }

  shareOnTwitter() {
    const message = encodeURIComponent(
      `${this.shareMessage} ${this.pageUrl}`
    );
    const url = `https://twitter.com/intent/tweet?text=${message}`;
    window.open(url, "_blank", "width=600,height=400");
    this.markAsShared('twitter');
  }

  copyShareLink() {
    const shareText = `${this.shareMessage}\n\nCheck it out: ${this.pageUrl}`;

    navigator.clipboard
      .writeText(shareText)
      .then(() => {
        const copyBtn = this.container.querySelector(
          `#share-copy-${this.sectionId}`
        );
        if (copyBtn) {
          const originalText = copyBtn.textContent;
          copyBtn.textContent = "Link Copied!";
          setTimeout(() => {
            copyBtn.textContent = originalText;
          }, 2000);
        }
        alert("Share link copied to clipboard!");
        this.markAsShared('copy');
      })
      .catch(() => {
        alert("Failed to copy. Please copy manually:\n\n" + shareText);
      });
  }

  async markAsShared(platform = 'unknown') {
    // Prevent duplicate share tracking
    if (this.hasTrackedShare) {
      console.log('⚠️ Share already tracked, skipping duplicate');
      return;
    }
    
    this.hasShared = true;
    this.hasTrackedShare = true; // Mark as tracked
    localStorage.setItem(`has_shared_${this.sectionId}`, "true");

    // NEW: Send to backend API
    try {
      await fetch(`${API_BASE_URL}/api/share/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: this.getSessionId(),
          platform: platform,
          discountCode: this.discountCode
        })
      });
      console.log('✅ Share tracked:', platform);
    } catch (error) {
      console.error('❌ Error tracking share:', error);
    }

    // Show coupon code after sharing
    setTimeout(() => {
      this.showCouponCode();
    }, 1000);
  }

  showCouponCode() {
    this.hasReceivedCoupon = true;
    localStorage.setItem(`has_received_coupon_${this.sectionId}`, "true");

    // Hide share prompt
    const sharePrompt = this.container.querySelector(
      `#share-prompt-container-${this.sectionId}`
    );
    if (sharePrompt) sharePrompt.style.display = "none";

    // Show success/coupon container
    const successContainer = this.container.querySelector(
      `#success-container-${this.sectionId}`
    );
    if (successContainer) {
      successContainer.style.display = "block";

      // Set discount code
      const discountCodeElement = this.container.querySelector(
        `#discount-code-${this.sectionId}`
      );
      if (discountCodeElement) {
        discountCodeElement.textContent = this.discountCode;
      }

      // Rate Again button now in HTML, no need to create dynamically


      // Scroll to coupon
      successContainer.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  setupCopyButton() {
    const copyBtn = this.container.querySelector(
      `#copy-code-${this.sectionId}`
    );
    if (!copyBtn) return;

    copyBtn.addEventListener("click", () => {
      const discountCodeElement = this.container.querySelector(
        `#discount-code-${this.sectionId}`
      );
      if (!discountCodeElement) return;

      const code = discountCodeElement.textContent;
      navigator.clipboard
        .writeText(code)
        .then(() => {
          copyBtn.textContent = "Copied! Redirecting...";
          copyBtn.style.background = "rgba(76, 175, 80, 0.3)";

          setTimeout(() => {
            window.location.href = "/";
          }, 1500);
        })
        .catch(() => {
          alert("Failed to copy code. Please copy manually: " + code);
        });
    });
  }

  setupApplyDiscountButton() {
    const applyBtn = this.container.querySelector(
      `#apply-discount-${this.sectionId}`
    );
    if (!applyBtn) return;

    applyBtn.addEventListener("click", async () => {
      const discountCodeElement = this.container.querySelector(
        `#discount-code-${this.sectionId}`
      );
      if (!discountCodeElement) return;

      const code = discountCodeElement.textContent;

      applyBtn.textContent = "Applying...";
      applyBtn.disabled = true;

      try {
        const response = await fetch(`/discount/${encodeURIComponent(code)}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          applyBtn.textContent = "Applied! Redirecting...";
          setTimeout(() => {
            window.location.href = "/";
          }, 1000);
        } else {
          throw new Error("Failed to apply discount");
        }
      } catch (error) {
        console.error("Error applying discount:", error);
        applyBtn.textContent = "Apply 10% Discount Now";
        applyBtn.disabled = false;
        alert(
          "Failed to apply discount. Please try copying the code and applying it at checkout."
        );
      }
    });
  }

  setupRateAgainButton() {
    const rateAgainLink = this.container.querySelector(
      `#rate-again-${this.sectionId}`
    );
    if (!rateAgainLink) return;

    rateAgainLink.addEventListener("click", (e) => {
      e.preventDefault(); // Prevent # navigation
      console.log('Rate Again link clicked - resetting ratings');
      this.resetRatings();
    });
  }

  saveRatings() {
    localStorage.setItem(
      `ratings_${this.sectionId}`,
      JSON.stringify(this.productRatings)
    );
  }

  loadExistingRatings() {
    // Clear completion flags on page load to allow re-rating
    localStorage.removeItem(`has_received_coupon_${this.sectionId}`);
    localStorage.removeItem(`has_shared_${this.sectionId}`);
    localStorage.removeItem(`has_submitted_${this.sectionId}`);
    
    // Load saved ratings (keep the ratings themselves)
    const saved = localStorage.getItem(`ratings_${this.sectionId}`);
    if (saved) {
      try {
        this.productRatings = JSON.parse(saved);
        // Restore star highlights for all products
        Object.keys(this.productRatings).forEach((productId) => {
          const rating = this.productRatings[productId];
          if (rating > 0) {
            this.highlightStars(productId, rating);
          }
        });
      } catch (e) {
        console.error("Error loading ratings:", e);
      }
    }

    // Always start fresh - show the first product
    this.showCurrentProduct();
    this.updateNextButton();
  }

  showRateAgainOption() {
    // Add "Rate Again" button to header
    const ratingsHeader = this.container.querySelector(".ratings-header");
    if (ratingsHeader && !ratingsHeader.querySelector(".rate-again-btn")) {
      const rateAgainBtn = document.createElement("button");
      rateAgainBtn.className = "btn btn-secondary rate-again-btn";
      rateAgainBtn.textContent = "Rate Again";
      rateAgainBtn.style.marginTop = "1rem";
      rateAgainBtn.addEventListener("click", () => {
        this.resetRatings();
      });
      ratingsHeader.appendChild(rateAgainBtn);
    }
  }

  resetRatings() {
    // Reset all ratings
    this.productRatings = {};
    this.currentProductIndex = 0;
    this.hasSubmitted = false; // Allow resubmission
    this.hasTrackedShare = false; // Allow resharing

    // Clear saved ratings
    localStorage.removeItem(`ratings_${this.sectionId}`);
    localStorage.removeItem(`has_submitted_${this.sectionId}`);
    localStorage.removeItem(`has_shared_${this.sectionId}`);
    localStorage.removeItem(`has_received_coupon_${this.sectionId}`);

    // Reset star highlights
    this.products.forEach((card) => {
      const productId = card.dataset.productId;
      this.highlightStars(productId, 0);
    });

    // Hide coupon and share prompts
    const successContainer = this.container.querySelector(
      `#success-container-${this.sectionId}`
    );
    const sharePrompt = this.container.querySelector(
      `#share-prompt-container-${this.sectionId}`
    );
    if (successContainer) successContainer.style.display = "none";
    if (sharePrompt) sharePrompt.style.display = "none";

    // Show rating container
    const ratingContainer = this.container.querySelector(
      `#rating-container-${this.sectionId}`
    );
    if (ratingContainer) ratingContainer.style.display = "block";

    // Show first product
    this.showCurrentProduct();
    this.updateProgress();
    this.updateNextButton();

    // Scroll to top
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  checkAndShowCompletionPage() {
    // Check if all 10 products have been rated
    const allRated = Object.keys(this.productRatings).length === this.totalProducts &&
                     Object.values(this.productRatings).every(rating => rating > 0);
    
    if (allRated) {
      console.log('All products already rated - showing coupon page');
      
      // Hide rating container
      const ratingContainer = this.container.querySelector(
        `#rating-container-${this.sectionId}`
      );
      if (ratingContainer) ratingContainer.style.display = "none";
      
      // Show success/coupon page directly
      this.showCouponCode();
      // Rate Again button is now in HTML
    }
  }


  // Helper: Get or create session ID
  getSessionId() {
    let sessionId = localStorage.getItem('ratings_session_id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('ratings_session_id', sessionId);
    }
    return sessionId;
  }

  // Helper: Get product titles
  getProductTitles() {
    const titles = {};
    this.products.forEach(card => {
      const productId = card.dataset.productId;
      const titleEl = card.querySelector('.product-name');
      if (titleEl) titles[productId] = titleEl.textContent.trim();
    });
    return titles;
  }
}

// Shopify section event handling
document.addEventListener("shopify:section:load", (event) => {
  const ratingsPages = event.target.querySelectorAll(".ratings-page");
  ratingsPages.forEach((page) => {
    // Prevent double initialization
    if (page.dataset.initialized === 'true') {
      console.log('⚠️ RatingsPage already initialized, skipping');
      return;
    }
    
    const sectionId =
      page.dataset.sectionId || page.id.replace("ratings-page-", "");
    if (!page.dataset.sectionId) {
      page.dataset.sectionId = sectionId;
    }
    page.dataset.initialized = 'true'; // Mark as initialized
    new RatingsPage(page);
  });
});

// Also check after page load (for dynamic content)
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    const ratingsPages = document.querySelectorAll(".ratings-page");
    ratingsPages.forEach((page) => {
      // Prevent double initialization
      if (page.dataset.initialized === 'true') {
        console.log('⚠️ RatingsPage already initialized, skipping');
        return;
      }
      
      const sectionId =
        page.dataset.sectionId || page.id.replace("ratings-page-", "");
      if (!page.dataset.sectionId) {
        page.dataset.sectionId = sectionId;
      }
      page.dataset.initialized = 'true'; // Mark as initialized
      new RatingsPage(page);
    });
  });
} else {
  const ratingsPages = document.querySelectorAll(".ratings-page");
  ratingsPages.forEach((page) => {
    // Prevent double initialization
    if (page.dataset.initialized === 'true') {
      console.log('⚠️ RatingsPage already initialized, skipping');
      return;
    }
    
    const sectionId =
      page.dataset.sectionId || page.id.replace("ratings-page-", "");
    if (!page.dataset.sectionId) {
      page.dataset.sectionId = sectionId;
    }
    page.dataset.initialized = 'true'; // Mark as initialized
    new RatingsPage(page);
  });
}
